import { IImpressionsByNetworkID } from '@/common/models/deliveryStatistics/DeliveryStatistics';
import { toServerDate } from '@/globals';

import Plan from './Plan';

interface IBreakdownDataAPI {
  ecpm: number;
  min_ecpm: number;
  max_ecpm: number;
  unit_count: number;
  unit_physical_count: number;
  site_count: number;
  impressions: number;
  impressions_by_network_id: IImpressionsByNetworkID[];
  audience_concentration: Record<string, number>;
}

class PlanBreakdownData {
  ecpm: number;
  minEcpm: number;
  maxEcpm: number;
  unitCount: number;
  unitPhysicalCount: number;
  siteCount: number;
  impressions: number;
  impressionsByNetworkID: IImpressionsByNetworkID[];
  audienceConcentration: Record<string, number>;

  constructor(
    ecpm: number,
    minEcpm: number,
    maxEcpm: number,
    unitCount: number,
    unitPhysicalCount: number,
    siteCount: number,
    impressions: number,
    impressionsByNetworkID: IImpressionsByNetworkID[],
    audienceConcentration: Record<string, number>,
  ) {
    this.ecpm = ecpm;
    this.minEcpm = minEcpm;
    this.maxEcpm = maxEcpm;
    this.unitCount = unitCount;
    this.unitPhysicalCount = unitPhysicalCount;
    this.siteCount = siteCount;
    this.impressions = impressions;
    this.impressionsByNetworkID = impressionsByNetworkID;
    this.audienceConcentration = audienceConcentration;
  }

  static toApi(plan: Plan) {
    const obj = {
      organization_id: plan.organizationID,
      min_concentration_factor: plan.minConcentrationFactor,
      measurement_requested: plan.isMeasurementRequested,
      max_bid_cpm: plan.maxBidCPM ?? null,
      min_concentration_value: plan.minConcentrationValue,
      to_date: toServerDate(plan.endDate),
      plan_id: plan.id != -1 ? plan.id : null,
      demographic_ids: plan.demographicIDs,
      ad_duration: plan.adDuration ?? 0,
      breakdown_type: plan.breakdownType,
      purchase_type: plan.purchaseType,
      network_frequency_caps: plan.networkFrequencyCaps.map((x) => x.toApi()),
      from_date: toServerDate(plan.startDate),
      screen_frequency_cap: plan.minSecondsBetweenPlays,
      buy_from_deals_enabled: plan.buyFromDealsEnabled,
      daypart: plan.daypart,
      creative_formats: plan.creativeFormats,
      creative_format_exclusions: plan.creativeFormatExclusions,
      creative_orientation_exclusions: plan.creativeOrientationExclusions,
      creative_orientations: plan.creativeOrientations,
      geofencegroup_ids: plan.geofenceGroupIDs,
      geofencegroup_exclusion_ids: plan.geofenceGroupExclusionIDs,
      iab_categories: plan.iabCategories,
      iab_category_exclusions: plan.iabCategoryExclusions,
      language_ids: plan.languageIDs,
      language_exclusion_ids: plan.languageExclusionIDs,
      location_ids: plan.locationIDs,
      location_exclusion_ids: plan.locationExclusionIDs,
      market_ids: plan.marketIDs,
      market_exclusion_ids: plan.marketExclusionIDs,
      mediatype_ids: plan.mediaTypeIDs,
      mediatype_exclusion_ids: plan.mediaTypeExclusionIDs,
      network_ids: plan.networkIDs,
      network_exclusion_ids: plan.networkExclusionIDs,
      owner_ids: plan.mediaOwnerIDs,
      site_ids: plan.siteIDs,
      site_exclusion_ids: plan.siteExclusionIDs,
      unit_ids: plan.unitIDs,
      unit_exclusion_ids: plan.unitExclusionIDs,
      unit_tags: plan.unitTags,
      unit_tag_exclusions: plan.unitTagExclusions,
      unitpack_ids: plan.unitPackIDs,
      unitpack_exclusion_ids: plan.unitPackExclusionIDs,
      package_id: plan.packageID ?? null,
      packagegroup_id: plan.multiDealsParams.packagegroup_id ?? null,
      package_ids: plan.multiDealsParams.package_ids ?? null,
    };

    return obj;
  }

  static fromApi(apiObject: IBreakdownDataAPI): PlanBreakdownData {
    return new PlanBreakdownData(
      apiObject.ecpm,
      apiObject.min_ecpm,
      apiObject.max_ecpm,
      apiObject.unit_count,
      apiObject.unit_physical_count,
      apiObject.site_count,
      apiObject.impressions,
      apiObject.impressions_by_network_id,
      apiObject.audience_concentration,
    );
  }
}

export default PlanBreakdownData;
